import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, X, ZoomIn, Ruler } from "lucide-react";
import { FLOOR_PLANS } from "@/data/property";
import { useSite } from "@/context/SiteContext";

function PlanZoom({ levelId, onClose }) {
  const [levelIdx, setLevelIdx] = useState(FLOOR_PLANS.findIndex((l) => l.id === levelId));
  const [zoomed, setZoomed] = useState(false);
  const level = FLOOR_PLANS[levelIdx];

  useEffect(() => {
    const i = FLOOR_PLANS.findIndex((l) => l.id === levelId);
    if (i >= 0) setLevelIdx(i);
  }, [levelId]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 p-4 backdrop-blur-sm"
    >
      <div className="flex items-center justify-between py-2">
        <p className="text-xs uppercase tracking-[0.25em] text-white/70">
          Official iGuide Floor Plans — 52-461 Beechwood Pl
        </p>
        <button
          data-testid="floorplan-zoom-close-button"
          aria-label="Close floor plan"
          onClick={onClose}
          className="rounded-full border border-white/30 p-2 text-white transition-colors duration-200 hover:bg-white/10"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 py-2">
        {FLOOR_PLANS.map((l, i) => (
          <button
            key={l.id}
            data-testid={`floorplan-zoom-tab-${l.id}`}
            onClick={() => {
              setLevelIdx(i);
              setZoomed(false);
            }}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${
              i === levelIdx
                ? "border-bronze bg-bronze text-white"
                : "border-white/30 text-white/80 hover:bg-white/10"
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div
        className="flex flex-1 items-center justify-center overflow-hidden py-2"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.img
          key={level.id}
          src={level.image}
          alt={`${level.label} floor plan`}
          data-testid="floorplan-zoom-image"
          onDoubleClick={() => setZoomed((z) => !z)}
          drag={zoomed}
          dragElastic={0.15}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, scale: zoomed ? 2 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className={`max-h-[68vh] max-w-full rounded-2xl bg-white object-contain ${
            zoomed ? "cursor-grab" : "cursor-zoom-in"
          }`}
        />
      </div>
      <p className="pb-2 text-center text-[11px] uppercase tracking-[0.2em] text-white/70">
        Double-click to zoom — drag to pan when zoomed
      </p>
    </motion.div>
  );
}

export default function FloorPlans() {
  const [levelId, setLevelId] = useState(FLOOR_PLANS[0].id);
  const [zoomLevel, setZoomLevel] = useState(null);
  const { floorPlanLevel } = useSite();

  useEffect(() => {
    if (floorPlanLevel) {
      setLevelId(floorPlanLevel.id);
      setZoomLevel(floorPlanLevel.id);
    }
  }, [floorPlanLevel]);

  const level = FLOOR_PLANS.find((l) => l.id === levelId);

  return (
    <div className="mt-20" data-testid="floorplans-links-section">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
            <span className="h-px w-10 bg-bronze" /> Floor Plans
          </p>
          <h3 className="font-display text-2xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-3xl">
            Official iGuide plans, one tap away
          </h3>
        </div>
        <p className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
          <Ruler size={15} className="text-bronze" /> Tap a level to open the plan
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {FLOOR_PLANS.map((l) => (
          <button
            key={l.id}
            data-testid={`floorplan-card-${l.id}`}
            onClick={() => {
              setLevelId(l.id);
              setZoomLevel(l.id);
            }}
            className={`group overflow-hidden rounded-2xl border text-left transition-colors duration-200 ${
              levelId === l.id
                ? "border-bronze"
                : "border-zinc-200 hover:border-bronze dark:border-zinc-800 dark:hover:border-bronze"
            }`}
          >
            <div className="relative h-44 overflow-hidden bg-white">
              <img
                src={l.image}
                alt={`${l.label} floor plan`}
                loading="lazy"
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white backdrop-blur-sm transition-colors duration-200 group-hover:bg-bronze">
                Open plan <ArrowUpRight size={12} />
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 bg-white p-4 dark:bg-zinc-900">
              <div>
                <p className="font-display text-base font-semibold text-zinc-900 dark:text-white">
                  {l.label}
                </p>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  {l.interior} interior · {l.exterior} exterior
                </p>
              </div>
              <ZoomIn size={16} className="flex-none text-bronze" />
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={level.id}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="mt-6 rounded-2xl border border-zinc-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900 sm:p-8"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-bronze">{level.label}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full bg-bronze/10 px-4 py-1.5 text-xs font-semibold text-bronze">
              Interior — {level.interior}
            </span>
            <span className="rounded-full bg-zinc-100 px-4 py-1.5 text-xs font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
              Exterior — {level.exterior}
            </span>
          </div>
          <ul className="mt-5 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {level.rooms.map((r) => (
              <li key={r.name} className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-300">
                <Check size={14} className="mt-0.5 flex-none text-bronze" />
                <span>
                  <span className="font-semibold text-zinc-900 dark:text-white">{r.name}</span>
                  {" — "}
                  {r.dims}
                  {r.sqft && (
                    <span className="ml-1.5 rounded-full bg-bronze/10 px-2 py-0.5 text-xs font-semibold text-bronze">
                      {r.sqft}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-6 border-t border-zinc-200 pt-4 text-[11px] leading-relaxed text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
            Prepared 2026-10-15 via iGuide. All room dimensions and floor areas are approximate and
            subject to independent verification.
          </p>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence>
        {zoomLevel && (
          <PlanZoom levelId={zoomLevel} onClose={() => setZoomLevel(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
